import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  listSessions,
  getSession,
  createSession,
  startSession,
  stopSession,
  deleteSession,
} from '@/api/sessions';
import type {
  CreateSessionRequest,
  StartSessionRequest,
} from '@/types';

export function useSessions() {
  return useQuery({
    queryKey: ['sessions'],
    queryFn: () => listSessions(),
    refetchInterval: 5_000,
  });
}

// Sessions a given agent took part in (transmitter or any stream's receiver) —
// the agent detail page's history.
export function useAgentSessions(agentId: string | undefined) {
  return useQuery({
    queryKey: ['sessions', 'agent', agentId],
    queryFn: () => listSessions(50, agentId!),
    enabled: !!agentId,
    refetchInterval: 5_000,
  });
}

export function useSession(id: string | undefined) {
  return useQuery({
    queryKey: ['session', id],
    queryFn: () => getSession(id!),
    enabled: !!id,
    // poll faster while a test is active so status transitions show up
    refetchInterval: (q) => (q.state.data?.status === 'active' ? 2_000 : false),
  });
}

export function useCreateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateSessionRequest) => createSession(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  });
}

export function useStartSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; req: StartSessionRequest }) =>
      startSession(args.id, args.req),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['sessions'] });
      qc.setQueryData(['session', s.id], s);
    },
  });
}

export function useStopSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => stopSession(id),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['sessions'] });
      qc.setQueryData(['session', s.id], s);
    },
  });
}

export function useDeleteSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSession(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['sessions'] });
      qc.removeQueries({ queryKey: ['session', id] });
    },
  });
}
